import { CircularProgress, Grid, Menu, MenuItem, Box, Typography, Button } from '@mui/material'
import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import MoreVert from '@mui/icons-material/MoreVert'
import Print from '@mui/icons-material/Print'
import Visibility from '@mui/icons-material/Visibility'
import Download from '@mui/icons-material/Download'
import ContentCopy from '@mui/icons-material/ContentCopy'
import PersonRounded from '@mui/icons-material/PersonRounded'
import DescriptionRounded from '@mui/icons-material/DescriptionRounded'
import ViewSidebarRounded from '@mui/icons-material/ViewSidebarRounded'
import VerticalAlignTopRounded from '@mui/icons-material/VerticalAlignTopRounded'
import VerticalAlignBottomRounded from '@mui/icons-material/VerticalAlignBottomRounded'
import TuneRounded from '@mui/icons-material/TuneRounded'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectEmrSectionsData,
  selectPrescriptionViewSettingsData,
} from 'src/clinic/reducers/prescription/prescriptionReducer'
import { fetchEmrSections } from 'src/clinic/reducers/prescription/services/emrSectionThunk'
import { CustomHTMLSelect } from 'src/consumer/components/helperComponents'
import StyledAccordionSectionWrapper from 'src/mentor/components/MentorPrescriptionTemplates/components/StyledAccordionSectionWrapper'
import PdfPreviewDialog from 'src/mentor/components/MentorPrescriptionTemplates/components/formComponents/PdfPreviewDialog'
import PrescriptionPadPreviewTabs from 'src/mentor/components/MentorPrescriptionTemplates/components/formComponents/PrescriptionPadPreviewTabs'
import PrescriptionPreviewScaleWrapper from 'src/mentor/components/MentorPrescriptionTemplates/components/formComponents/PrescriptionPreviewScaleWrapper'
import usePdfPrintTemplate from 'src/mentor/components/MentorPrescriptionTemplates/components/hooks/usePdfPrintTemplate'
import usePrescriptionDataForTemplate from 'src/mentor/components/MentorPrescriptionTemplates/components/hooks/usePrescriptionDataForTemplate'
import MultipleTemplateHeadersComponent from 'src/mentor/components/MentorPrescriptionTemplates/components/sections/MultipleTemplateHeadersComponent'
import PrescriptionBodySection from 'src/mentor/components/MentorPrescriptionTemplates/components/sections/PrescriptionBodySection'
import TemplatePatientDetailsSection from 'src/mentor/components/MentorPrescriptionTemplates/components/sections/TemplatePatientDetailsSection'
import {
  ActuallyPrintableContainer,
  SplitEditorContainer,
  FormControlsContainer,
  TemplateSelectorWrapper,
  StyledTemplateSelect,
  ActionButtonGroup,
  PurpleOutlinedButton,
  PurpleContainedButton,
  SaveButtonContainer,
  PurpleIconButton,
} from 'src/mentor/components/MentorPrescriptionTemplates/components/styledComponents'
import ViewOnlyPrescriptionV2 from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/ViewOnlyPrescriptionV2'
import ClinicDetailsSection from './sections/ClinicDetailsSection'
import CustomMarginsSection from './sections/CustomMarginsSection'
import DoctorDetailsSection from './sections/DoctorDetailsSection'
import FooterDetailsSection from './sections/FooterDetailsSection'
import HeaderDetailsSection from './sections/HeaderDetailsSection'
import PrescriptionLayoutSection from './sections/PrescriptionLayoutSection'
import SidebarDetailsSection from './sections/SidebarDetailsSection'
import SignatureDetailsSection from './sections/SignatureDetailsSection'
import { resolvePrescriptionPrintLanguage } from 'src/mentor/components/PrescriptionMakingPage/inputComponents/drugAdvice/rxExplainer'

/**
 * Main form component for prescription template
 * Orchestrates all section components in a clean, maintainable way
 */
export default function PrescriptionTemplateForm({
  printPreviewType,
  setPrintPreviewType,
  prescriptionTemplate,
  setPrescriptionTemplate,
  copyFromPaperTemplate,
  canCopyFromPaper = false,
  supportPractitionerSlug,
  pdfOptions,
  printOptions,
  handlers,
  templateActions,
  customOptions = {},
  isUsingNewPrintMethod = false,
  disabled = false,
  clinicId,
  savePrescriptionTemplate,
  handleSubmit,
  mentorProfileData,
  eventId,
  pdfPreviewApiFn,
  pdfPreviewApiEndpoint,
  pdfPreviewExtraParams,
  emrSectionsApiSignature,
  emrSettingsSaveApiSignature,
}) {
  const dispatch = useDispatch()
  const templateOptions = (printPreviewType === 'pdf' ? pdfOptions : printOptions) || []
  const isDigitalTab = printPreviewType === 'pdf'
  const [showPdfPrintDialog, setPdfPrintDialog] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const menuOpen = Boolean(menuAnchorEl)
  // Page-break tuning flags — fixed defaults (the UI toggles were niche/confusing for
  // clinicians configuring a template, so they were removed). Best-fit pagination is on.
  const useEmptyBox = false
  const useBestFit = true

  const { downloadPdf, downloadingPdf } = usePdfPrintTemplate({
    prescriptionTemplateId: prescriptionTemplate?.id,
    clinicId,
    useEmptyBox,
    apiFn: pdfPreviewApiFn,
    apiEndpoint: pdfPreviewApiEndpoint,
    extraParams: pdfPreviewExtraParams,
  })

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const previewContentRef = useRef(null)
  const printableContainerRef = useRef(null)

  // Independent scrolling for the two panes is now pure CSS: the route is a bounded
  // 100dvh flex column (see PrescriptionViewPage's EditorPageContainer/EditorFormArea), so
  // SplitEditorContainer fills the remaining height via flex:1/min-height:0 and each pane
  // owns its own scrollbar (overflow-y:auto + min-height:0). No JS measurement needed.
  const serializeWithComputedStyles = useCallback((sourceEl) => {
    const clone = sourceEl.cloneNode(true)
    let headerBannerMarked = false
    let bodyFound = false
    let patientSectionFound = false
    // Pre-scan: collect all elements that are ancestors of .final-printable, INCLUDING
    // sourceEl itself. These wrapper elements absorb decorative spacing (margin, padding,
    // min-height from the live preview panel) into their computed size, making the
    // serialized HTML taller than A4. We skip height/min-height/margin/padding on all
    // of them so the new tab collapses to just the prescription content height (~1122px).
    const finalPrintableEl = sourceEl.querySelector?.('.final-printable')
    const finalPrintableAncestors = new Set()
    if (finalPrintableEl) {
      let _el = finalPrintableEl.parentElement
      while (_el) {
        finalPrintableAncestors.add(_el)
        if (_el === sourceEl) break
        _el = _el.parentElement
      }
    }
    const walk = (orig, copy) => {
      if (!orig || !copy || orig.nodeType !== 1 || copy.nodeType !== 1) return
      // Reset section-attribution flags at each .final-printable boundary so every
      // page gets its own correct header/patient/body attributes (not just page 1).
      const isFinalPrintableEl = typeof orig.className === 'string' && orig.className.split(' ').includes('final-printable')
      if (isFinalPrintableEl) {
        bodyFound = false
        patientSectionFound = false
        headerBannerMarked = false
      }
      const cs = window.getComputedStyle(orig)
      const cn = typeof orig.className === 'string' ? orig.className : ''
      const insideFooter = orig.closest && orig.closest('[data-print-footer="true"]')
      const hasBodyAttr = orig.getAttribute?.('data-prescription-body') === 'true'
      const hasPatientAttr = orig.getAttribute?.('data-prescription-patient-section') === 'true'
      if (hasBodyAttr) {
        copy.setAttribute('data-prescription-body', 'true')
        bodyFound = true
      } else if (/PmpConsultationBodyContentContainer|PmpContentContainer/i.test(cn)) {
        copy.setAttribute('data-prescription-body', 'true')
        bodyFound = true
      }
      if (hasPatientAttr) {
        copy.setAttribute('data-prescription-patient-section', 'true')
        patientSectionFound = true
      } else if (/PMPHeaderDetails|PmpHeaderDetails/i.test(cn) && !clone.querySelector?.('[data-prescription-patient-section="true"]')) {
        copy.setAttribute('data-prescription-patient-section', 'true')
        patientSectionFound = true
      }
      if (!bodyFound && !patientSectionFound && !copy.getAttribute('data-prescription-body') && !copy.getAttribute('data-prescription-patient-section') && !insideFooter) {
        copy.setAttribute('data-prescription-header', 'true')
      }
      const isHeadingBannerClass = typeof orig.className === 'string' && /HeadingBanner/i.test(orig.className) && orig.children.length > 0
      const isHeadingBannerStyle = (cs.textAlign === 'center' || cs.textAlign === 'centre') && parseInt(cs.fontWeight, 10) >= 700 && cs.borderBottomWidth && parseFloat(cs.borderBottomWidth) > 0
      if (!headerBannerMarked && (isHeadingBannerClass || isHeadingBannerStyle)) {
        copy.setAttribute('data-prescription-header-banner', 'true')
        headerBannerMarked = true
      }
      if (insideFooter && (cs.display === 'flex' || cs.display === 'grid')) copy.setAttribute('data-prescription-layout', cs.display)
      if (/footer-spacer-rectangle/i.test(cn) || orig.getAttribute?.('data-print-spacer') === 'true') copy.setAttribute('data-prescription-spacer', 'true')
      const parts = []
      // Layout
      if (cs.display) parts.push(`display:${cs.display}`)
      if (cs.width && cs.width !== 'auto') parts.push(`width:${cs.width}`)
      if (cs.minWidth && cs.minWidth !== '0px') parts.push(`min-width:${cs.minWidth}`)
      if (cs.maxWidth && cs.maxWidth !== 'none') parts.push(`max-width:${cs.maxWidth}`)
      /* Skip height for:
         1. Elements with flex-grow (expand freely to fill available space).
         2. Ancestors of .final-printable: their computed height absorbs margin-space
            from child elements (e.g. A4PageBackgroundContainer's margin:20px), making
            them taller than A4 height (1123px). Leaving their height as auto lets
            them collapse to their true content size during print. */
      const isGrowingFlex = cs.flexGrow && cs.flexGrow !== '0'
      const isFinalPrintableAncestor = finalPrintableAncestors.has(orig)
      if (!isGrowingFlex && !isFinalPrintableAncestor && cs.height && cs.height !== 'auto' && cs.height !== '0px') parts.push(`height:${cs.height}`)
      if (!isFinalPrintableAncestor && cs.minHeight && cs.minHeight !== '0px') parts.push(`min-height:${cs.minHeight}`)
      if (cs.maxHeight && cs.maxHeight !== 'none') parts.push(`max-height:${cs.maxHeight}`)
      // Flexbox
      if (cs.flexDirection && (cs.display === 'flex' || cs.display === 'inline-flex')) parts.push(`flex-direction:${cs.flexDirection}`)
      if (cs.justifyContent && (cs.display === 'flex' || cs.display === 'inline-flex')) parts.push(`justify-content:${cs.justifyContent}`)
      if (cs.alignItems && (cs.display === 'flex' || cs.display === 'inline-flex')) parts.push(`align-items:${cs.alignItems}`)
      if (cs.alignSelf && cs.alignSelf !== 'auto') parts.push(`align-self:${cs.alignSelf}`)
      if (cs.flexWrap && cs.flexWrap !== 'nowrap') parts.push(`flex-wrap:${cs.flexWrap}`)
      if (cs.flexGrow && cs.flexGrow !== '0') parts.push(`flex-grow:${cs.flexGrow}`)
      if (cs.flexShrink && cs.flexShrink !== '1') parts.push(`flex-shrink:${cs.flexShrink}`)
      if (cs.flexBasis && cs.flexBasis !== 'auto') parts.push(`flex-basis:${cs.flexBasis}`)
      // Grid
      if (cs.display === 'grid' && cs.gridTemplateColumns) parts.push(`grid-template-columns:${cs.gridTemplateColumns}`)
      if (cs.display === 'grid' && cs.gridTemplateRows) parts.push(`grid-template-rows:${cs.gridTemplateRows}`)
      if (cs.gridColumn && cs.gridColumn !== 'auto') parts.push(`grid-column:${cs.gridColumn}`)
      if (cs.gridRow && cs.gridRow !== 'auto') parts.push(`grid-row:${cs.gridRow}`)
      // Position
      if (cs.position && cs.position !== 'static') {
        parts.push(`position:${cs.position}`)
        if (cs.top && cs.top !== 'auto') parts.push(`top:${cs.top}`)
        if (cs.right && cs.right !== 'auto') parts.push(`right:${cs.right}`)
        if (cs.bottom && cs.bottom !== 'auto') parts.push(`bottom:${cs.bottom}`)
        if (cs.left && cs.left !== 'auto') parts.push(`left:${cs.left}`)
        if (cs.zIndex && cs.zIndex !== 'auto') parts.push(`z-index:${cs.zIndex}`)
      }
      // Box model
      if (cs.boxSizing) parts.push(`box-sizing:${cs.boxSizing}`)
      if (cs.overflow && cs.overflow !== 'visible') parts.push(`overflow:${cs.overflow}`)
      if (cs.overflowX && cs.overflowX !== 'visible') parts.push(`overflow-x:${cs.overflowX}`)
      if (cs.overflowY && cs.overflowY !== 'visible') parts.push(`overflow-y:${cs.overflowY}`)
      if (!isFinalPrintableAncestor && cs.paddingTop && cs.paddingTop !== '0px') parts.push(`padding:${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft}`)
      /* Skip margin on ancestors of .final-printable — their decorative margin (e.g.
         A4PageBackgroundContainer: margin:20px auto) inflates the wrapper height in the
         new tab, causing overflow onto a blank 2nd page during print. Content elements
         inside .final-printable still get their own margins serialized normally. */
      if (!isFinalPrintableAncestor && (cs.marginTop !== '0px' || cs.marginRight !== '0px' || cs.marginBottom !== '0px' || cs.marginLeft !== '0px')) {
        parts.push(`margin:${cs.marginTop} ${cs.marginRight} ${cs.marginBottom} ${cs.marginLeft}`)
      }
      // Borders
      if (parseFloat(cs.borderTopWidth) > 0 && cs.borderTopStyle !== 'none') parts.push(`border-top:${cs.borderTopWidth} ${cs.borderTopStyle} ${cs.borderTopColor}`)
      if (parseFloat(cs.borderRightWidth) > 0 && cs.borderRightStyle !== 'none') parts.push(`border-right:${cs.borderRightWidth} ${cs.borderRightStyle} ${cs.borderRightColor}`)
      if (parseFloat(cs.borderBottomWidth) > 0 && cs.borderBottomStyle !== 'none') parts.push(`border-bottom:${cs.borderBottomWidth} ${cs.borderBottomStyle} ${cs.borderBottomColor}`)
      if (parseFloat(cs.borderLeftWidth) > 0 && cs.borderLeftStyle !== 'none') parts.push(`border-left:${cs.borderLeftWidth} ${cs.borderLeftStyle} ${cs.borderLeftColor}`)
      if (cs.borderCollapse) parts.push(`border-collapse:${cs.borderCollapse}`)
      if (cs.borderSpacing && cs.borderSpacing !== '0px 0px') parts.push(`border-spacing:${cs.borderSpacing}`)
      if (cs.borderRadius && cs.borderRadius !== '0px') parts.push(`border-radius:${cs.borderRadius}`)
      // Background — skip white/transparent (page default) to keep debug section colors visible
      const bgColor = cs.backgroundColor
      const _isTransparent = !bgColor || bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent'
      const _isWhite = /^rgba?\(255,\s*255,\s*255[^)]*\)$/.test(bgColor) || bgColor === 'rgb(255, 255, 255)'
      if (!_isTransparent && !_isWhite) parts.push(`background-color:${bgColor}`)
      if (cs.backgroundImage && cs.backgroundImage !== 'none') {
        parts.push(`background-image:${cs.backgroundImage}`)
        if (cs.backgroundSize) parts.push(`background-size:${cs.backgroundSize}`)
        if (cs.backgroundRepeat) parts.push(`background-repeat:${cs.backgroundRepeat}`)
        if (cs.backgroundPosition) parts.push(`background-position:${cs.backgroundPosition}`)
      }
      // Typography
      if (cs.fontSize) parts.push(`font-size:${cs.fontSize}`)
      if (cs.fontWeight && cs.fontWeight !== '400') parts.push(`font-weight:${cs.fontWeight}`)
      if (cs.fontStyle && cs.fontStyle !== 'normal') parts.push(`font-style:${cs.fontStyle}`)
      if (cs.fontFamily) parts.push(`font-family:${cs.fontFamily}`)
      if (cs.lineHeight && cs.lineHeight !== 'normal') parts.push(`line-height:${cs.lineHeight}`)
      if (cs.letterSpacing && cs.letterSpacing !== 'normal') parts.push(`letter-spacing:${cs.letterSpacing}`)
      if (cs.textAlign && cs.textAlign !== 'start') parts.push(`text-align:${cs.textAlign}`)
      if (cs.textDecoration && cs.textDecoration !== 'none solid rgba(0, 0, 0, 0)' && cs.textDecorationLine !== 'none') parts.push(`text-decoration:${cs.textDecoration}`)
      if (cs.textOverflow && cs.textOverflow !== 'clip') parts.push(`text-overflow:${cs.textOverflow}`)
      if (cs.textTransform && cs.textTransform !== 'none') parts.push(`text-transform:${cs.textTransform}`)
      if (cs.whiteSpace && cs.whiteSpace !== 'normal') parts.push(`white-space:${cs.whiteSpace}`)
      if (cs.wordBreak && cs.wordBreak !== 'normal') parts.push(`word-break:${cs.wordBreak}`)
      if (cs.color) parts.push(`color:${cs.color}`)
      if (cs.verticalAlign && cs.verticalAlign !== 'baseline') parts.push(`vertical-align:${cs.verticalAlign}`)
      // Table
      if (cs.tableLayout && cs.tableLayout !== 'auto') parts.push(`table-layout:${cs.tableLayout}`)
      // Visibility
      if (cs.visibility && cs.visibility !== 'visible') parts.push(`visibility:${cs.visibility}`)
      // Transforms (non-scale ones for layout purposes)
      if (cs.transform && cs.transform !== 'none' && !cs.transform.startsWith('matrix')) parts.push(`transform:${cs.transform}`)
      if (parts.length) copy.setAttribute('style', (copy.getAttribute('style') || '') + (copy.getAttribute('style') ? ';' : '') + parts.join(';'))
      // For ancestors of .final-printable, forcefully override size-defining properties at the END of inline style.
      // cloneNode(true) preserves original inline styles (height, padding, margin from the live DOM),
      // and our conditional skips above only prevent us from *appending* them — they don't remove what was already there.
      // By appending these overrides LAST, we win the CSS last-declaration rule and neutralize any original inline values.
      if (isFinalPrintableAncestor) {
        // overflow:visible prevents overflow:auto on ancestors from spawning a scrollbar
        // when serialized children are slightly wider than the ancestor box (e.g. prescription-page border).
        // A scrollbar would add ~15px to getBoundingClientRect().height, inflating the total print height.
        copy.setAttribute('style', (copy.getAttribute('style') ? copy.getAttribute('style') + ';' : '') + 'height:auto;min-height:0;margin:0;padding:0;overflow:visible')
      }
      let co = orig.firstChild, cc = copy.firstChild
      while (co && cc) {
        if (isFinalPrintableAncestor && co.nodeType === 1 && cc.nodeType === 1) {
          const isChildOnPath = finalPrintableAncestors.has(co) || co.classList.contains('final-printable')
          if (!isChildOnPath) {
            // Non-path sibling inside an ancestor: hide it and skip recursion.
            cc.setAttribute('style', (cc.getAttribute('style') ? cc.getAttribute('style') + ';' : '') + 'display:none')
            co = co.nextSibling
            cc = cc.nextSibling
            continue
          }
        }
        walk(co.nodeType === 1 ? co : null, cc.nodeType === 1 ? cc : null)
        co = co.nextSibling
        cc = cc.nextSibling
      }
    }
    walk(sourceEl, clone)
    /* Remove header from elements that contain body/patient section.
       querySelectorAll does NOT match the root element itself, so check clone explicitly too. */
    const cleanupHeaderMark = (el) => {
      if (el.getAttribute?.('data-prescription-header') === 'true') {
        if (el.querySelector?.('[data-prescription-body="true"]') || el.querySelector?.('[data-prescription-patient-section="true"]')) {
          el.removeAttribute('data-prescription-header')
        }
      }
    }
    cleanupHeaderMark(clone)
    clone.querySelectorAll?.('[data-prescription-header="true"]').forEach(cleanupHeaderMark)
    return clone.outerHTML
  }, [])

  const openPrintPreviewInNewWindow = useCallback((shouldPrint) => {
    const previewRoot = previewContentRef.current?.firstElementChild || previewContentRef.current
    const el = previewRoot || printableContainerRef.current
    if (!el) {
      if (shouldPrint) window.print()
      return
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const baseTag = origin ? `<base href="${origin}/" />` : ''

    // Identify visible .final-printable pages by checking ancestor visibility.
    // Styled components (like PMPConsultationParent) may have visibility:visible in their CSS,
    // overriding an ancestor's visibility:hidden. Walking up to the boundary element (el)
    // catches hidden containers that getComputedStyle on the element alone would miss.
    const liveFps = el?.querySelectorAll ? Array.from(el.querySelectorAll('.final-printable')) : []
    const visibleLiveFps = liveFps.filter(fp => {
      let isAncestorHidden = false
      let node = fp.parentElement
      while (node && node !== el) {
        const pcs = window.getComputedStyle(node)
        if (pcs.visibility === 'hidden' || pcs.display === 'none') { isAncestorHidden = true; break }
        node = node.parentElement
      }
      const fpCs = window.getComputedStyle(fp)
      return !isAncestorHidden && fpCs.visibility !== 'hidden' && fpCs.display !== 'none'
    })

    // Serialize each visible page individually so every page gets its own computed-style
    // walk. Serializing the whole tree caused the walk's ancestor-path-skipping logic
    // (designed for single-page Rx) to hide page 2+ wrapper elements, leaving those pages
    // without serialized computed styles (no display:flex, no colors, no heights).
    const pageHtmls = visibleLiveFps.map(fp => serializeWithComputedStyles(fp))

    let containerHtml = ''
    if (pageHtmls.length > 0) {
      containerHtml = `<div style="display:block;width:794px;margin:0;padding:0;overflow:visible;box-sizing:border-box;">${pageHtmls.join('')}</div>`
    }

    // Per-section debug CSS — uses ::before overlay (screen only) so the color renders ON TOP of
    // child elements with opaque backgrounds. @media screen ensures the overlay never prints,
    // so the actual prescription content is always clean in print/PDF output.
    const mkSection = (sel, color, border) => `
@media screen{${sel}{position:relative!important;outline:2px dashed ${border}!important;}}
@media screen{${sel}::before{content:'';position:absolute;inset:0;background:${color}!important;pointer-events:none;z-index:9999;}}
@media screen{${sel} ${sel}::before{display:none!important;}}
@media screen{${sel} ${sel}{outline:none!important;}}`
    const sectionDebugCss = {
      header: mkSection('[data-prescription-header="true"]', 'rgba(60,130,255,0.35)', '#06c') +
        `\n[data-prescription-header-banner="true"],[class*="HeadingBanner"]{outline:3px solid #06c!important;}`,
      patient: mkSection('[data-prescription-patient-section="true"]', 'rgba(255,200,50,0.55)', '#b80'),
      body: mkSection('[data-prescription-body="true"]', 'rgba(0,200,80,0.28)', '#060'),
      spacer: mkSection('[data-prescription-spacer="true"]', 'rgba(255,120,0,0.35)', '#a50'),
      footer: mkSection('[data-print-footer="true"]', 'rgba(220,50,150,0.35)', '#c06'),
    }

    const baseStyles = `*{box-sizing:border-box;}body{margin:0;padding:0;background:#f0f0f0;font-family:system-ui,sans-serif;}#debug-toolbar{padding:10px 14px;background:#fff;border-bottom:2px solid #ddd;position:sticky;top:0;z-index:100;display:flex;flex-wrap:wrap;align-items:center;gap:10px;}.hide-on-print{display:none!important;}.dbg-height-chip{display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:600;padding:2px 7px;border-radius:10px;color:#fff;}.dbg-height-sep{width:1px;height:20px;background:#ddd;display:inline-block;}
/* Prevent positioned children from stacking above debug section background (screen only, not print) */
@media screen{[data-prescription-header="true"] *:not(table,thead,tbody,tfoot,tr,td,th,colgroup,col),[data-prescription-patient-section="true"] *:not(table,thead,tbody,tfoot,tr,td,th,colgroup,col),[data-prescription-body="true"] *:not(table,thead,tbody,tfoot,tr,td,th,colgroup,col),[data-print-footer="true"] *:not(table,thead,tbody,tfoot,tr,td,th,colgroup,col){position:relative!important;z-index:0!important;}}
@page{margin:0mm;}@media print{#debug-toolbar{display:none!important;}body{margin:0!important;padding:0!important;background:transparent!important;}.final-printable{display:flex!important;flex-direction:column!important;min-height:297mm;height:auto!important;overflow:visible!important;}.final-printable+.final-printable{page-break-before:always!important;}[data-prescription-spacer="true"],[data-print-spacer="true"]{flex:1 1 auto!important;min-height:0!important;}[data-print-footer="true"]{flex-shrink:0!important;margin-top:auto!important;}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}}`

    // Inline style tags as JS-managed strings so the toggle script can disable them
    const sectionStyleTags = Object.entries(sectionDebugCss).map(([k, css]) =>
      `<style id="dbg-${k}">${css}</style>`
    ).join('')

    // Raw toggle script content (no <script> tags) — reused for both iframe and overlay modes
    const toggleScriptContent = `(function(){
  var DPI=96;
  var A4_W_IN=8.27;
  var A4_H_IN=11.69;
  var A4_W_PX=794;
  var A4_H_PX=1122;
  var sectionMeta={
    header:{label:'\u{1F535} Header',sel:'[data-prescription-header="true"]',bg:'#3c82ff'},
    patient:{label:'\u{1F7E1} Patient',sel:'[data-prescription-patient-section="true"]',bg:'#c8960a'},
    body:{label:'\u{1F7E2} Body',sel:'[data-prescription-body="true"]',bg:'#00a850'},
    spacer:{label:'\u{1F7E0} Spacer',sel:'[data-prescription-spacer="true"]',bg:'#d06800'},
    footer:{label:'\u{1F534} Footer',sel:'[data-print-footer="true"]',bg:'#c03280'},
  };
  var toolbar=document.getElementById('debug-toolbar');
  if(!toolbar)return;
  Object.keys(sectionMeta).forEach(function(k){
    var m=sectionMeta[k];
    var lbl=document.createElement('label');
    lbl.style.cssText='display:inline-flex;align-items:center;gap:5px;cursor:pointer;font-size:13px;font-weight:500;user-select:none;';
    var cb=document.createElement('input');
    cb.type='checkbox'; cb.checked=true; cb.style.cssText='width:15px;height:15px;cursor:pointer;accent-color:#7450aa;';
    cb.addEventListener('change',function(){
      var el=document.getElementById('dbg-'+k);
      if(el) el.disabled=!cb.checked;
      updateHeights();
    });
    lbl.appendChild(cb); lbl.appendChild(document.createTextNode(' '+m.label));
    toolbar.appendChild(lbl);
  });
  // Page indicator
  var sep=document.createElement('span'); sep.className='dbg-height-sep'; toolbar.appendChild(sep);
  var pageIndicator=document.createElement('span');
  pageIndicator.id='dbg-page-indicator';
  pageIndicator.style.cssText='font-size:13px;font-weight:700;color:#7450aa;padding:2px 10px;border-radius:10px;background:#f0ebff;white-space:nowrap;';
  toolbar.appendChild(pageIndicator);
  var sep2=document.createElement('span'); sep2.className='dbg-height-sep'; toolbar.appendChild(sep2);
  var hLbl=document.createElement('label');
  hLbl.style.cssText='display:inline-flex;align-items:center;gap:5px;cursor:pointer;font-size:13px;font-weight:500;user-select:none;';
  var hCb=document.createElement('input');
  hCb.type='checkbox'; hCb.id='tog-heights'; hCb.style.cssText='width:15px;height:15px;cursor:pointer;accent-color:#7450aa;';
  hCb.addEventListener('change',function(){ updateHeights(); });
  hLbl.appendChild(hCb); hLbl.appendChild(document.createTextNode(' \uD83D\uDCCF Heights'));
  toolbar.appendChild(hLbl);
  var chipRow=document.createElement('div');
  chipRow.id='dbg-height-row';
  chipRow.style.cssText='display:flex;flex-wrap:wrap;gap:5px;width:100%;padding-top:4px;';
  toolbar.appendChild(chipRow);

  var contentEl=document.getElementById('rx-print-content');
  // Scope to drawer only — the main page also has .final-printable elements in the live preview
  var allPages=contentEl?Array.from(contentEl.querySelectorAll('.final-printable')):[];
  var currentPageIdx=0;

  function getCurrentPageIdx(){
    if(!contentEl||allPages.length===0) return 0;
    var contentRect=contentEl.getBoundingClientRect();
    var bestIdx=0; var bestOverlap=-1;
    allPages.forEach(function(page,idx){
      var rect=page.getBoundingClientRect();
      var overlapTop=Math.max(contentRect.top,rect.top);
      var overlapBottom=Math.min(contentRect.bottom,rect.bottom);
      var overlap=Math.max(0,overlapBottom-overlapTop);
      if(overlap>bestOverlap){bestOverlap=overlap;bestIdx=idx;}
    });
    return bestIdx;
  }

  function updatePageIndicator(){
    currentPageIdx=getCurrentPageIdx();
    pageIndicator.textContent='Page '+(currentPageIdx+1)+' of '+allPages.length;
  }

  function updateHeights(){
    chipRow.innerHTML='';
    if(!document.getElementById('tog-heights').checked) return;
    var currentPage=allPages[currentPageIdx]||null;
    Object.keys(sectionMeta).forEach(function(k){
      var m=sectionMeta[k];
      var styleEl=document.getElementById('dbg-'+k);
      var colorOn=!styleEl||!styleEl.disabled;
      // Prefer section within the current viewport page; fall back to first visible in drawer
      var target=null;
      if(currentPage){
        var inPage=currentPage.querySelectorAll(m.sel);
        for(var i=0;i<inPage.length;i++){
          var r=inPage[i].getBoundingClientRect();
          if(r.width>0&&r.height>0){target=inPage[i];break;}
        }
      }
      if(!target&&contentEl){
        var els=contentEl.querySelectorAll(m.sel);
        for(var j=0;j<els.length;j++){
          var r2=els[j].getBoundingClientRect();
          if(r2.width>0&&r2.height>0){target=els[j];break;}
        }
      }
      if(!target) return;
      var rect=target.getBoundingClientRect();
      var hIn=(rect.height/DPI).toFixed(2);
      var wIn=A4_W_IN.toFixed(2);
      var chip=document.createElement('span');
      chip.className='dbg-height-chip';
      chip.style.background=colorOn?m.bg:'#999';
      chip.textContent=m.label.split(' ')[1]+': '+hIn+'in \xD7 '+wIn+'in';
      chipRow.appendChild(chip);
    });
    var pageEl=currentPage||(contentEl&&contentEl.querySelector('.final-printable'));
    if(pageEl){
      var r=pageEl.getBoundingClientRect();
      var remH=Math.round(A4_H_PX-r.height);
      var remW=Math.round(A4_W_PX-r.width);
      var remHIn=(remH/DPI).toFixed(2);
      var remWIn=(remW/DPI).toFixed(2);
      var a4Chip=document.createElement('span');
      a4Chip.className='dbg-height-chip';
      a4Chip.style.background='#555';
      a4Chip.textContent='A4: '+A4_H_IN+' \xD7 '+A4_W_IN+' in';
      chipRow.appendChild(a4Chip);
      var remChip=document.createElement('span');
      remChip.className='dbg-height-chip';
      remChip.style.background=remH<=0&&remW<=0?'#0a0':'#c00';
      remChip.textContent='Remaining: '+remHIn+' \xD7 '+remWIn+' in';
      chipRow.appendChild(remChip);
    }
  }

  if(contentEl){
    contentEl.addEventListener('scroll',function(){
      updatePageIndicator();
      updateHeights();
    });
  }
  updatePageIndicator();
})();`

    if (shouldPrint) {
      // Immediate print via hidden iframe
      const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8">${baseTag}<title>Prescription Print</title><style>${baseStyles}</style>${sectionStyleTags}</head><body>${containerHtml}</body></html>`
      const iframe = document.createElement('iframe')
      iframe.setAttribute('style', 'position:fixed;left:0;top:0;width:794px;height:1200px;z-index:2147483647;background:#fff;border:0;')
      document.body.appendChild(iframe)
      const doc = iframe.contentWindow?.document
      if (doc) {
        doc.open()
        doc.write(fullHtml)
        doc.close()
        iframe.contentWindow.onload = () => {
          setTimeout(() => { iframe.contentWindow?.print(); document.body.removeChild(iframe) }, 400)
        }
      } else document.body.removeChild(iframe)
      return
    }

    // Preview: inject a full-screen overlay into the current page instead of opening a new tab.
    // This avoids Safari's blob-URL instability (tab blanks after a few seconds) and
    // document.write() blank-on-parameter-change issues. The main page has a stable localhost
    // URL so Safari's print renderer re-renders correctly on any parameter change.
    const OVERLAY_ID = 'rx-print-overlay'
    const STYLE_ID = 'rx-print-overlay-style'

    // Clean up any previous overlay
    document.getElementById(OVERLAY_ID)?.remove()
    document.getElementById(STYLE_ID)?.remove()
    document.querySelectorAll('[data-rx-print-overlay]').forEach(el => el.remove())

    // Inject per-section debug styles into the main page head
    Object.entries(sectionDebugCss).forEach(([k, css]) => {
      const el = document.createElement('style')
      el.id = `dbg-${k}`
      el.setAttribute('data-rx-print-overlay', '')
      el.textContent = css
      document.head.appendChild(el)
    })

    // Drawer + print CSS
    const MODAL_ID = 'rx-print-modal'
    const CONTENT_ID = 'rx-print-content'
    const styleEl = document.createElement('style')
    styleEl.id = STYLE_ID
    styleEl.textContent = [
      // Backdrop — starts transparent, animates to semi-opaque
      `#${OVERLAY_ID}{position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,0);transition:background 0.3s ease;}`,
      `#${OVERLAY_ID}.rx-open{background:rgba(0,0,0,0.45);}`,
      // Drawer panel — slides in from the right on desktop
      `#${MODAL_ID}{position:absolute;top:0;right:0;bottom:0;width:min(100%,900px);background:#fff;display:flex;flex-direction:column;box-shadow:-8px 0 48px rgba(0,0,0,0.22);transform:translateX(100%);transition:transform 0.3s cubic-bezier(0.4,0,0.2,1);overflow:hidden;}`,
      `#${OVERLAY_ID}.rx-open #${MODAL_ID}{transform:translateX(0);}`,
      // Drawer panel — slides up from the bottom on mobile
      `@media(max-width:768px){#${MODAL_ID}{top:auto;left:0;right:0;width:100%;max-height:92vh;border-radius:16px 16px 0 0;transform:translateY(100%);box-shadow:0 -8px 48px rgba(0,0,0,0.22);}#${OVERLAY_ID}.rx-open #${MODAL_ID}{transform:translateY(0);}}`,
      // Sticky toolbar
      `#${OVERLAY_ID} #debug-toolbar{padding:12px 48px 12px 16px;background:#fff;border-bottom:1px solid #eee;display:flex;flex-wrap:wrap;align-items:center;gap:10px;flex-shrink:0;}`,
      // Close button pinned top-right of the drawer panel
      `#rx-print-close-btn{position:absolute;top:10px;right:10px;width:30px;height:30px;border-radius:50%;border:none;background:#f0f0f0;color:#555;font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:1;}`,
      `#rx-print-close-btn:hover{background:#e0e0e0;color:#111;}`,
      // Scrollable content area — flex centering, pages stack vertically inside
      `#${CONTENT_ID}{overflow-y:auto;flex:1;background:#f4f4f5;padding:24px;display:flex;flex-direction:column;align-items:center;}`,
      // Wrapper: block so each .final-printable stacks vertically (not flex siblings)
      `#${CONTENT_ID}>div{display:block!important;}`,
      // Each .final-printable is one A4 page. Let serialized flex layout handle internal positioning
      // (header/body/spacer/footer). min-height:297mm ensures short pages fill a full A4 sheet.
      `#${CONTENT_ID} .final-printable{min-height:297mm;overflow:visible!important;}`,
      // Gap between pages for visual clarity in the drawer
      `#${CONTENT_ID} .final-printable+.final-printable{margin-top:16px!important;}`,
      // Spacer grows inside its page's flex column to push footer to bottom
      `#${CONTENT_ID} [data-prescription-spacer="true"],#${CONTENT_ID} [data-print-spacer="true"]{flex:1 1 auto!important;min-height:0!important;}`,
      // Footer stays pinned to bottom of its page
      `#${CONTENT_ID} [data-print-footer="true"]{flex-shrink:0!important;margin-top:auto!important;}`,
      `.dbg-height-chip{display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:600;padding:2px 7px;border-radius:10px;color:#fff;}`,
      `.dbg-height-sep{width:1px;height:20px;background:#ddd;display:inline-block;}`,
      `@page{margin:0mm;}`,
      `@media print{`,
      `  html,body{position:static!important;top:0!important;left:0!important;margin:0!important;padding:0!important;overflow:visible!important;height:auto!important;}`,
      `  body>*:not(#${OVERLAY_ID}){display:none!important;}`,
      `  #${OVERLAY_ID}{position:static!important;overflow:visible!important;background:transparent!important;display:block!important;}`,
      `  #${MODAL_ID}{position:static!important;box-shadow:none!important;max-height:none!important;overflow:visible!important;display:block!important;transform:none!important;width:100%!important;}`,
      `  #${OVERLAY_ID} #debug-toolbar,#rx-print-close-btn{display:none!important;}`,
      `  #${CONTENT_ID}{overflow:visible!important;padding:0!important;background:transparent!important;display:block!important;}`,
      // Preserve flex layout so spacer grows and footer stays pinned to bottom of each page.
      // display:block broke the layout — header/patient sections overlapped body (collapsed spacer).
      `  .final-printable{display:flex!important;flex-direction:column!important;min-height:297mm;height:auto!important;overflow:visible!important;}`,
      `  .final-printable+.final-printable{page-break-before:always!important;}`,
      `  [data-prescription-spacer="true"],[data-print-spacer="true"]{flex:1 1 auto!important;min-height:0!important;}`,
      `  [data-print-footer="true"]{flex-shrink:0!important;margin-top:auto!important;}`,
      `  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}`,
      `}`,
    ].join('\n')
    document.head.appendChild(styleEl)

    // Unique close-function name to avoid conflicts with any existing global
    const closeFnName = `_rxPrintClose${Date.now()}`
    const escHandler = (e) => { if (e.key === 'Escape') window[closeFnName]?.() }
    window[closeFnName] = () => {
      document.removeEventListener('keydown', escHandler)
      delete window[closeFnName]
      delete window[printFnName]
      const overlayEl = document.getElementById(OVERLAY_ID)
      if (overlayEl) {
        overlayEl.classList.remove('rx-open')
        // Wait for the backdrop fade-out transition, then remove everything
        const onTransitionDone = (e) => {
          if (e.target !== overlayEl) return
          overlayEl.removeEventListener('transitionend', onTransitionDone)
          overlayEl.remove()
          document.getElementById(STYLE_ID)?.remove()
          document.querySelectorAll('[data-rx-print-overlay]').forEach(el => el.remove())
        }
        overlayEl.addEventListener('transitionend', onTransitionDone)
      } else {
        document.getElementById(STYLE_ID)?.remove()
        document.querySelectorAll('[data-rx-print-overlay]').forEach(el => el.remove())
      }
    }
    document.addEventListener('keydown', escHandler)

    // Print handler — uses an isolated iframe so Safari's print engine sees ONLY the prescription
    // HTML (no React app DOM). The main-window body injection approach failed because Safari
    // counted page height from the full document (3095px React DOM) rather than the 297mm
    // visible prescription element, producing a trailing blank second page.
    const printFnName = `_rxDoPrint${Date.now()}`
    window[printFnName] = () => {
      // Print CSS for the isolated iframe document.
      // zoom:0.999 (0.1% reduction) is the targeted fix for Safari's trailing blank page:
      // the serialized prescription content is ~0.96px taller than A4 (297mm = 1122.52px) in
      // Safari's print engine. Safari ignores overflow:hidden for page count, so the only reliable
      // fix is to reduce the content's layout size via zoom so page_count = ceil(0.999×content/A4) = 1.
      // User confirmed 99% print-dialog scale works; zoom:0.999 is the CSS equivalent (0.3mm impact).
      const iframePrintCss = [
        '@page{size:A4 portrait;margin:0mm;}',
        '@media print{',
        'html{zoom:0.999;margin:0!important;padding:0!important;}',
        'body{margin:0!important;padding:0!important;}',
        // Strip serialized debug outlines.
        '[data-prescription-header="true"],[data-prescription-patient-section="true"],[data-prescription-body="true"],[data-prescription-spacer="true"],[data-print-footer="true"]{outline:none!important;}',
        '*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}',
        '}',
      ].join('\n')

      const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${iframePrintCss}</style></head><body style="margin:0;padding:0;">${containerHtml}</body></html>`

      const iframe = document.createElement('iframe')
      // Off-screen but not display:none — print() requires the frame to be visible to the engine.
      iframe.setAttribute('style', 'position:fixed;left:-9999px;top:0;width:794px;height:1px;border:0;')
      document.body.appendChild(iframe)

      const iDoc = iframe.contentWindow?.document
      if (!iDoc) { iframe.remove(); return }

      iDoc.open()
      iDoc.write(fullHtml)
      iDoc.close()

      const doPrint = () => {
        iframe.contentWindow?.print()
        // Clean up after print dialog closes (afterprint fires on the iframe's window).
        iframe.contentWindow?.addEventListener('afterprint', () => { iframe.remove() }, { once: true })
        // Fallback removal in case afterprint doesn't fire (some browsers).
        setTimeout(() => { iframe.remove() }, 60000)
      }

      if (iDoc.readyState === 'complete') {
        doPrint()
      } else {
        iframe.onload = doPrint
      }
    }

    // Build drawer DOM
    const overlay = document.createElement('div')
    overlay.id = OVERLAY_ID
    // Click on the backdrop (overlay itself, not the drawer panel) to close
    overlay.addEventListener('click', (e) => { if (e.target === overlay) window[closeFnName]?.() })
    overlay.innerHTML = `
      <div id="${MODAL_ID}">
        <button id="rx-print-close-btn" onclick="${closeFnName}()" title="Close (Esc)">\u2715</button>
        <div id="debug-toolbar">
          <span style="font-size:13px;font-weight:700;color:#333;">Prescription Preview</span>
          <span class="dbg-height-sep"></span>
          <button onclick="${printFnName}()" style="background:#7450aa;color:#fff;border:none;border-radius:6px;padding:6px 14px;font-size:13px;font-weight:600;cursor:pointer;">Print</button>
          <span class="dbg-height-sep"></span>
        </div>
        <div id="${CONTENT_ID}">
          <div style="width:794px;">${containerHtml}</div>
        </div>
      </div>`
    document.body.appendChild(overlay)
    // Trigger entrance animation on next frame (after browser has painted the initial state)
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => overlay.classList.add('rx-open')))

    // Run the toggle script in the current page context via a <script> element
    const scriptEl = document.createElement('script')
    scriptEl.setAttribute('data-rx-print-overlay', '')
    scriptEl.textContent = toggleScriptContent
    document.body.appendChild(scriptEl)
  }, [serializeWithComputedStyles])

  const currentEmrSectionsData = useSelector(selectEmrSectionsData)
  const reduxEmrSections = (currentEmrSectionsData?.data || {})?.prescription?.emrSections || []
  // Live (unsaved) section enable/print state pushed up from the Sections list so the
  // preview reflects toggles immediately; falls back to the saved redux sections.
  const [liveEmrSections, setLiveEmrSections] = useState(null)
  const currentEmrSections =
    liveEmrSections && liveEmrSections.length > 0 ? liveEmrSections : reduxEmrSections

  // Removed console.log for performance - logs large objects on every render

  const prescriptionViewSettingsData = useSelector(selectPrescriptionViewSettingsData)
  const prescriptionViewSettings = prescriptionViewSettingsData?.data
  // Single language the practitioner-wide EMR settings would print in — used to pre-fill the
  // template's "Preferred Language" field when it hasn't set its own.
  const practitionerDefaultLanguage = resolvePrescriptionPrintLanguage(prescriptionViewSettings, null)
  const isSupportPortal =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/support-home') || window.location.pathname.startsWith('/support/'))
  const {
    currentTemplateWithDummyData,
    loadingPrescriptionData,
    prescriptionDataLoadError,
    refetchPrescriptionData,
    regenerateSampleData,
  } = usePrescriptionDataForTemplate({
    templateData: prescriptionTemplate,
    useEmptyBox,
    isSupportPortal,
    practitionerId: mentorProfileData?.id,
    practitionerSlug: mentorProfileData?.slug || supportPractitionerSlug,
  })

  const showOverlayOnTheRightSide = loadingPrescriptionData
  // Show the preview error notice only once loading has settled and no data came back.
  const showPreviewLoadError =
    !loadingPrescriptionData &&
    !!prescriptionDataLoadError &&
    !currentTemplateWithDummyData?.prescriptionData

  useEffect(() => {
    // Detect portal and pass practitionerId/practitionerSlug for support portal
    const currentPortal = typeof window !== 'undefined' && 
      (window.location.pathname.startsWith('/support-home') || window.location.pathname.startsWith('/support/'))
        ? 'support'
        : 'mentor'
    
    if (currentPortal === 'support' && mentorProfileData) {
      const params = {}
      if (mentorProfileData.slug) {
        params.practitionerSlug = mentorProfileData.slug
      } else if (mentorProfileData.id) {
        params.practitionerId = mentorProfileData.id
      }
      if (Object.keys(params).length > 0) {
        dispatch(fetchEmrSections(params))
      }
    } else {
      dispatch(fetchEmrSections())
    }
  }, [mentorProfileData?.id, mentorProfileData?.slug])

  // Render Manasthan layout form separately THIS IS NOT IMPLEMENTED ON THE NEW UI YET. NEED TO DO THIS LATER IF REQUIRED.
  // if (prescriptionTemplate?.layout === PRESCRIPTION_LAYOUTS.manasthan) {
  //   return (
  //     <Grid container spacing={2}>
  //       <ManasthanPrescriptionTemplateForm
  //         customOptions={customOptions}
  //         prescriptionTemplate={prescriptionTemplate}
  //         setPrescriptionTemplate={setPrescriptionTemplate}
  //         handleChangeObject={handlers.handleChange}
  //         handleNestedChangeObject={handlers.handleNestedChange}
  //         disabled={disabled}
  //       />
  //       <Grid item xs={12} />
  //       <PrescriptionPreview prescriptionTemplate={prescriptionTemplate} />
  //     </Grid>
  //   )
  // }

  // Default layout form
  return (
    <>
      <SplitEditorContainer className="hide-on-print-only">
        <FormControlsContainer
          className="hide-on-print-only"
          key={prescriptionTemplate?.id}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div>
                <PrescriptionPadPreviewTabs
                  previewValue={printPreviewType}
                  setPreviewValue={setPrintPreviewType}
                />
              </div>
              {/* On the Digital Rx tab, offer to clone the Paper template's design here. */}
              {isDigitalTab && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 1.5,
                    mb: 0.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <PurpleOutlinedButton
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopy />}
                    disabled={disabled || !canCopyFromPaper}
                    onClick={() => copyFromPaperTemplate?.()}
                  >
                    Copy from Paper Template
                  </PurpleOutlinedButton>
                </Box>
              )}
              <TemplateSelectorWrapper>
                <StyledTemplateSelect>
                  <CustomHTMLSelect
                    id="template"
                    outerMostDivStyle={{ width: '100%' }}
                    value={prescriptionTemplate?.id || ''}
                    style={{
                      background: 'white',
                      WebkitAppearance: 'auto',
                      padding: '8px 8px',
                      width: '100%',
                    }}
                  options={
                    !prescriptionTemplate?.id
                      ? [{ id: '', title: 'New Prescription...' }, ...templateOptions]
                      : templateOptions
                  }
                  getOptionValue={(optn) => optn?.id}
                  getOptionLabel={(optn) =>
                    optn?.id === ''
                      ? optn?.title
                      : `${optn?.active ? '(ACTIVE) ' : ''}${optn?.title || 'Untitled Template'}`
                  }
                  onChange={(e) => {
                    const foundTemplate = templateOptions?.find((tpl) => tpl.id === e.target.value)
                    if (foundTemplate) {
                      setPrescriptionTemplate(foundTemplate)
                    }
                  }}
                  />
                </StyledTemplateSelect>
                {prescriptionTemplate?.id && (
                  <>
                    <PurpleIconButton
                      size="small"
                      onClick={handleMenuClick}
                      aria-controls={menuOpen ? 'template-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={menuOpen ? 'true' : undefined}
                    >
                      <MoreVert />
                    </PurpleIconButton>
                    <Menu
                      id="template-menu"
                      anchorEl={menuAnchorEl}
                      open={menuOpen}
                      onClose={handleMenuClose}
                      MenuListProps={{
                        'aria-labelledby': 'template-menu-button',
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          templateActions.addTemplate()
                          handleMenuClose()
                        }}
                      >
                        <Add fontSize="small" sx={{ mr: 1 }} />
                        Create New Template
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          templateActions.deleteTemplate()
                          handleMenuClose()
                        }}
                      >
                        <Delete fontSize="small" sx={{ mr: 1 }} />
                        Delete Template
                      </MenuItem>
                      {!prescriptionTemplate?.active && (
                        <MenuItem
                          onClick={() => {
                            templateActions.activateTemplate()
                            handleMenuClose()
                          }}
                        >
                          Activate Template
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </TemplateSelectorWrapper>
              <ActionButtonGroup>
                <PurpleOutlinedButton
                  variant="outlined"
                  size="small"
                  startIcon={<Print />}
                  onClick={async () => {
                    if (printPreviewType === 'pdf') {
                      setPdfPrintDialog(true)
                    } else {
                      openPrintPreviewInNewWindow(true)
                    }
                  }}
                >
                  Print
                </PurpleOutlinedButton>
                <PurpleOutlinedButton
                  variant="outlined"
                  size="small"
                  startIcon={<Visibility />}
                  onClick={async () => {
                    if (printPreviewType === 'pdf') {
                      await savePrescriptionTemplate({ enableSilentRefresh: true })
                      setPdfPrintDialog(true)
                    } else {
                      openPrintPreviewInNewWindow(false)
                    }
                  }}
                >
                  Preview
                </PurpleOutlinedButton>
                <PurpleOutlinedButton
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  disabled={downloadingPdf}
                  onClick={async () => {
                    if (printPreviewType === 'pdf') {
                      await savePrescriptionTemplate({ enableSilentRefresh: true })
                      await downloadPdf()
                    } else {
                      window.print()
                    }
                  }}
                >
                  {downloadingPdf ? 'Downloading...' : 'Download'}
                </PurpleOutlinedButton>
              </ActionButtonGroup>
            </Grid>
            <StyledAccordionSectionWrapper
              unmountOnExit
              heading="Header"
              icon={<VerticalAlignTopRounded />}
            >
              <HeaderDetailsSection
                prescriptionTemplate={prescriptionTemplate}
                handleChangeHeader={handlers.handleChangeHeader}
                isUsingNewPrintMethod={isUsingNewPrintMethod}
                disabled={disabled}
              />
              <DoctorDetailsSection
                prescriptionTemplate={prescriptionTemplate}
                handleChange={handlers.handleChange}
                handleChangeSecondDoctor={handlers.handleChangeSecondDoctor}
                disabled={disabled}
              />
              <SignatureDetailsSection
                prescriptionTemplate={prescriptionTemplate}
                handleChangeSignatureDetails={handlers.handleChangeSignatureDetails}
                handleSignatureRichTextChange={handlers.handleSignatureRichTextChange}
                disabled={disabled}
              />
              <ClinicDetailsSection
                prescriptionTemplate={prescriptionTemplate}
                handleChange={handlers.handleChange}
              />
              <MultipleTemplateHeadersComponent
                prescriptionTemplate={prescriptionTemplate}
                prescriptionTemplateHandlers={handlers}
                disabled={disabled}
              />
            </StyledAccordionSectionWrapper>
            <StyledAccordionSectionWrapper
              unmountOnExit
              heading="Patient Details"
              icon={<PersonRounded />}
            >
              <TemplatePatientDetailsSection
                prescriptionTemplate={prescriptionTemplate}
                prescriptionTemplateHandlers={handlers}
                // mentorProfileData={mentorProfileData}
                // refreshEmrSections={() => {
                //   dispatch(fetchEmrSections({ forceUpdateSections: true }))
                // }}
              />
            </StyledAccordionSectionWrapper>
            <StyledAccordionSectionWrapper
              defaultExpanded
              unmountOnExit
              heading="Body"
              icon={<DescriptionRounded />}
            >
              <PrescriptionBodySection
                prescriptionTemplate={prescriptionTemplate}
                prescriptionTemplateHandlers={handlers}
                availableEmrSections={currentEmrSections}
                inheritedStyleOverrides={prescriptionViewSettings?.styleOverridesForPrintedEmrSections}
                onLiveEmrSectionsChange={setLiveEmrSections}
                mentorProfileData={mentorProfileData}
                emrSectionsApiSignature={emrSectionsApiSignature}
                emrSettingsSaveApiSignature={emrSettingsSaveApiSignature}
                refreshEmrSections={() => {
                  const currentPortal = typeof window !== 'undefined' && 
                    (window.location.pathname.startsWith('/support-home') || window.location.pathname.startsWith('/support/'))
                      ? 'support'
                      : 'mentor'
                  
                  if (currentPortal === 'support' && mentorProfileData) {
                    const params = { forceUpdateSections: true }
                    if (mentorProfileData.slug) {
                      params.practitionerSlug = mentorProfileData.slug
                    } else if (mentorProfileData.id) {
                      params.practitionerId = mentorProfileData.id
                    }
                    if (Object.keys(params).length > 1) {
                      dispatch(fetchEmrSections(params))
                    } else {
                      dispatch(fetchEmrSections({ forceUpdateSections: true }))
                    }
                  } else {
                    dispatch(fetchEmrSections({ forceUpdateSections: true }))
                  }
                }}
              />
            </StyledAccordionSectionWrapper>
            <StyledAccordionSectionWrapper
              unmountOnExit
              heading="Sidebar"
              icon={<ViewSidebarRounded />}
            >
              <SidebarDetailsSection
                prescriptionTemplate={prescriptionTemplate}
                handleChangeLeftSidebar={handlers.handleChangeLeftSidebar}
                handleChangeRightSidebar={handlers.handleChangeRightSidebar}
                disabled={disabled}
              />
            </StyledAccordionSectionWrapper>
            <StyledAccordionSectionWrapper
              unmountOnExit
              heading="Footer"
              icon={<VerticalAlignBottomRounded />}
            >
              <FooterDetailsSection
                prescriptionTemplate={prescriptionTemplate}
                handleChangeFooter={handlers.handleChangeFooter}
                handleChangePrintFooterSettings={handlers.handleChangePrintFooterSettings}
                handleChangeRelatedClinicDetails={handlers.handleChangeRelatedClinicDetails}
                isUsingNewPrintMethod={isUsingNewPrintMethod}
                disabled={disabled}
              />
            </StyledAccordionSectionWrapper>
            {/* Layout = page margins + layout-type/print-type presets. Advanced, so it sits last
                and stays collapsed by default. */}
            <StyledAccordionSectionWrapper
              unmountOnExit
              heading="Layout (Advanced)"
              icon={<TuneRounded />}
            >
              <CustomMarginsSection
                prescriptionTemplate={prescriptionTemplate}
                handleChangeMargins={handlers.handleChangeMargins}
                defaultMargins={{
                  marginTop: prescriptionViewSettings?.paperPrintSettings?.marginTop,
                  marginBottom: prescriptionViewSettings?.paperPrintSettings?.marginBottom,
                  marginLeft: prescriptionViewSettings?.paperPrintSettings?.marginLeft,
                  marginRight: prescriptionViewSettings?.paperPrintSettings?.marginRight,
                }}
                disabled={disabled}
              />
              <PrescriptionLayoutSection
                prescriptionTemplate={prescriptionTemplate}
                handleChange={handlers.handleChange}
                customOptions={customOptions}
                disabled={disabled}
                practitionerDefaultLanguage={practitionerDefaultLanguage}
              />
            </StyledAccordionSectionWrapper>
            <Grid item xs={12} />
          </Grid>
          <SaveButtonContainer>
            <PurpleOutlinedButton
              variant="outlined"
              disabled={disabled}
              onClick={() => {
                // Reset = discard unsaved edits by reloading the saved version of the
                // currently-selected template.
                if (prescriptionTemplate?.id) {
                  const saved = templateOptions?.find((tpl) => tpl.id === prescriptionTemplate.id)
                  if (saved) {
                    setPrescriptionTemplate(saved)
                  }
                }
              }}
            >
              Reset
            </PurpleOutlinedButton>
            <PurpleContainedButton onClick={handleSubmit}>
              Save
            </PurpleContainedButton>
          </SaveButtonContainer>
        </FormControlsContainer>
        <div
          className="hide-on-print-only prescription-preview-panel"
          style={{
            flex: 1,
            minWidth: 0,
            // min-height:0 is REQUIRED so this flex child can shrink and own its scrollbar,
            // fully independent of the left controls panel.
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflowX: 'hidden',
            overflowY: 'auto',
            // Light-gray backdrop so the white A4 sheets float with their shadow and the
            // preview reads as a section clearly separated from the controls on the left.
            background: '#eceff3',
            borderRadius: 12,
            padding: '20px 0',
          }}
        >
          <style>
            {`
              .prescription-preview-panel [class*="A4WrapperContainer"] {
                overflow: visible !important;
              }
              .prescription-preview-panel [class*="A4WrapperContainer"] > [class*="MuiBox-root"] {
                overflow: visible !important;
              }
            `}
          </style>
          {/* Rebuild the sample/dummy data for this clinic (forceReinitialize). Floats over
              the preview, hidden on print, so it doesn't affect layout or serialization. */}
          <Button
            className="hide-on-print-only"
            size="small"
            variant="outlined"
            disabled={loadingPrescriptionData}
            onClick={async () => {
              await regenerateSampleData?.()
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 12,
              textTransform: 'none',
              borderRadius: 2,
              background: 'rgba(255,255,255,0.9)',
              fontSize: '12px',
            }}
          >
            ↻ Regenerate sample data
          </Button>
          <div
            className="hide-on-print-only"
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              filter: showOverlayOnTheRightSide ? 'blur(4px)' : 'none',
              pointerEvents: showOverlayOnTheRightSide ? 'none' : 'auto',
            }}
          >
            <PrescriptionPreviewScaleWrapper>
              <div ref={previewContentRef} style={{ width: '100%' }}>
              <ViewOnlyPrescriptionV2
                key={prescriptionTemplate?.id || 'preview'}
                  prescriptionData={currentTemplateWithDummyData}
                hidePrintMarginsControl
                prescriptionViewSettings={prescriptionViewSettings}
                showTheA4Page
                doctorEmrSections={currentEmrSections}
                useEmptyBox={useEmptyBox}
                useBestFit={useBestFit}
                practitionerId={mentorProfileData?.id}
              />
              </div>
            </PrescriptionPreviewScaleWrapper>
          </div>
          {showOverlayOnTheRightSide && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </div>
          )}
          {showPreviewLoadError && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                zIndex: 11,
                padding: 24,
              }}
            >
              <div style={{ maxWidth: 420, textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Preview unavailable
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {prescriptionDataLoadError?.message ||
                    'No prescription template is configured for this clinic or its clinic brand yet. Create one and every doctor in the brand will inherit it.'}
                </Typography>
                <Button variant="outlined" size="small" onClick={() => refetchPrescriptionData?.()}>
                  Retry
                </Button>
              </div>
            </div>
          )}
        </div>
      </SplitEditorContainer>
      <PdfPreviewDialog
        open={showPdfPrintDialog}
        prescriptionTemplateData={prescriptionTemplate}
        clinicId={clinicId}
        useEmptyBox={useEmptyBox}
        onClose={() => {
          setPdfPrintDialog(false)
        }}
        apiFn={pdfPreviewApiFn}
        apiEndpoint={pdfPreviewApiEndpoint}
        extraParams={pdfPreviewExtraParams}
      />
      <ActuallyPrintableContainer>
        <div ref={printableContainerRef}>
        <ViewOnlyPrescriptionV2
          key={prescriptionTemplate?.id || 'printable'}
            prescriptionData={currentTemplateWithDummyData}
          hidePrintMarginsControl
          prescriptionViewSettings={prescriptionViewSettings}
          doctorEmrSections={currentEmrSections}
          useEmptyBox={useEmptyBox}
          useBestFit={useBestFit}
          practitionerId={mentorProfileData?.id}
        />
        </div>
      </ActuallyPrintableContainer>
    </>
  )
}
